import React, { useState } from 'react'
import { CreditCard, Loader2, AlertCircle, Lock } from 'lucide-react'
import { loadStripe } from '@stripe/stripe-js'
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js'
import Modal from '../ui/Modal'
import { Button } from '../ui'
import { useToast } from '../../contexts/ToastContext'
import { paymentMethodService } from '../../services/paymentMethodService'

const STRIPE_PUBLIC_KEY = import.meta.env.VITE_STRIPE_PUBLIC_KEY || ''

// Initialize Stripe outside component to avoid recreating on each render
const stripePromise = STRIPE_PUBLIC_KEY ? loadStripe(STRIPE_PUBLIC_KEY) : null

interface AddPaymentMethodModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

interface CardFormProps {
  onSuccess: () => void
  onClose: () => void
}

const CardForm: React.FC<CardFormProps> = ({ onSuccess, onClose }) => {
  const stripe = useStripe()
  const elements = useElements()
  const { showError, showSuccess } = useToast()

  const [loading, setLoading] = useState(false)
  const [cardComplete, setCardComplete] = useState(false)
  const [cardError, setCardError] = useState<string | null>(null)
  const [setAsDefault, setSetAsDefault] = useState(true)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!stripe || !elements) {
      showError('Error', 'Payment form not ready. Please try again.')
      return
    }

    const cardElement = elements.getElement(CardElement)
    if (!cardElement) {
      showError('Error', 'Card element not found')
      return
    }

    setLoading(true)

    try {
      // Create payment method using Stripe.js
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      })

      if (error) {
        showError('Card Error', error.message || 'Failed to process card')
        return
      }

      if (!paymentMethod) {
        showError('Error', 'Failed to create payment method')
        return
      }

      // Send payment method to backend
      const response = await paymentMethodService.addPaymentMethod({
        paymentMethodId: paymentMethod.id,
        setAsDefault
      })

      if (response.success) {
        showSuccess('Payment Method Added', 'Your card has been added successfully.')
        onSuccess()
      } else {
        showError('Failed to Add Card', response.message || 'Please try again.')
      }
    } catch (error) {
      console.error('Error adding payment method:', error)
      showError('Error', 'Failed to add payment method. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setCardComplete(false)
    setCardError(null)
    onClose()
  }

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#374151',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        '::placeholder': {
          color: '#9CA3AF'
        }
      },
      invalid: {
        color: '#EF4444',
        iconColor: '#EF4444'
      }
    }
  }

  return (
    <form onSubmit={handleSubmit} className='space-y-4'>
      {/* Card Input */}
      <div>
        <label className='block text-sm font-medium text-gray-700 mb-2'>
          Card Information
        </label>
        <div className='p-3 border border-gray-300 rounded-lg bg-white focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500'>
          <CardElement
            options={cardElementOptions}
            onChange={(event) => {
              setCardComplete(event.complete)
              setCardError(event.error?.message || null)
            }}
          />
        </div>
        {cardError && (
          <p className='mt-1.5 text-sm text-red-600 flex items-center gap-1'>
            <AlertCircle className='w-3.5 h-3.5' />
            {cardError}
          </p>
        )}
      </div>

      {/* Set as Default Checkbox */}
      <label className='flex items-center gap-2 cursor-pointer'>
        <input
          type='checkbox'
          checked={setAsDefault}
          onChange={(e) => setSetAsDefault(e.target.checked)}
          className='w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500'
        />
        <span className='text-sm text-gray-700'>Set as default payment method</span>
      </label>

      {/* Security Note */}
      <div className='flex items-start gap-2 p-3 bg-gray-50 rounded-lg'>
        <Lock className='w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0' />
        <p className='text-xs text-gray-600'>
          Your payment information is encrypted and securely processed by Stripe.
          We never store your full card details.
        </p>
      </div>

      {/* Actions */}
      <div className='flex justify-end gap-2 pt-2'>
        <Button
          type='button'
          variant='outline'
          size='sm'
          onClick={handleClose}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          type='submit'
          variant='primary'
          size='sm'
          disabled={!cardComplete || loading || !stripe}
        >
          {loading ? (
            <>
              <Loader2 className='w-4 h-4 animate-spin mr-1.5' />
              Adding...
            </>
          ) : (
            <>
              <CreditCard className='w-4 h-4 mr-1.5' />
              Add Card
            </>
          )}
        </Button>
      </div>
    </form>
  )
}

const AddPaymentMethodModal: React.FC<AddPaymentMethodModalProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  if (!STRIPE_PUBLIC_KEY) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title='Add Payment Method'
        size='md'
      >
        <div className='flex items-center gap-3 p-4 bg-red-50 rounded-lg'>
          <AlertCircle className='w-5 h-5 text-red-500 flex-shrink-0' />
          <div>
            <p className='text-sm font-medium text-red-800'>Configuration Error</p>
            <p className='text-sm text-red-600'>
              Stripe is not configured. Please contact support.
            </p>
          </div>
        </div>
      </Modal>
    )
  }

  if (!stripePromise) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title='Add Payment Method'
        size='md'
      >
        <div className='flex items-center justify-center py-8'>
          <Loader2 className='w-6 h-6 animate-spin text-gray-500' />
          <span className='ml-2 text-gray-500'>Loading payment form...</span>
        </div>
      </Modal>
    )
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title='Add Payment Method'
      size='md'
    >
      <Elements stripe={stripePromise}>
        <CardForm onSuccess={onSuccess} onClose={onClose} />
      </Elements>
    </Modal>
  )
}

export default AddPaymentMethodModal
